"use client";

import {
  useEventListener,
  useLatest,
  useLocalStorageState,
} from "@nyntax/hooks";
import { ensureReady } from "@web-kits/audio";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";

import { isDelegatedEvent } from "./feedback-events";
import type { FeedbackEvent } from "./feedback-events";
import { triggerHaptic } from "./haptics";

/** Map a Base UI toast `type` to a feedback event (null = no feedback). */
function toastTypeToEvent(type: unknown): FeedbackEvent | null {
  switch (type) {
    case "success": {
      return "success";
    }

    case "error": {
      return "error";
    }

    case "warning": {
      return "warn";
    }

    case "info": {
      return "info";
    }

    default: {
      return null;
    }
  }
}

import { toastManager } from "../components/ui/toast";
import { playSound } from "./sounds";
import { FeedbackContext } from "./use-feedback";
import type { FeedbackContextValue, PlayOptions } from "./use-feedback";

/** Minimum ms between identical fires of the same channel+event (anti machine-gun). */
const THROTTLE_MS = 40;

/**
 *
 */
export interface FeedbackProviderProps {
  children: ReactNode;
  /** Override the default-on state for sound (first load only). */
  defaultSound?: boolean;
  /** Override the default-on state for haptic (first load only). */
  defaultHaptic?: boolean;
}

/**
 *
 */
export function FeedbackProvider({
  children,
  defaultSound = true,
  defaultHaptic = true,
}: FeedbackProviderProps) {
  const [sound = defaultSound, setSound] = useLocalStorageState<boolean>(
    "feedback:sound",
    { defaultValue: defaultSound }
  );

  const [haptic = defaultHaptic, setHaptic] = useLocalStorageState<boolean>(
    "feedback:haptic",
    { defaultValue: defaultHaptic }
  );

  // Latest values in refs so the long-lived document listeners never go stale.
  const soundRef = useLatest(sound);
  const hapticRef = useLatest(haptic);
  const hiddenRef = useRef(false);
  const audioReadyRef = useRef(false);
  const lastFiredRef = useRef<Map<string, number>>(new Map());

  const throttled = useCallback((key: string): boolean => {
    const now = Date.now();
    const last = lastFiredRef.current.get(key) ?? 0;

    if (now - last < THROTTLE_MS) {
      return true;
    }

    lastFiredRef.current.set(key, now);

    return false;
  }, []);

  const fireHaptic = useCallback(
    (event: FeedbackEvent) => {
      if (!hapticRef.current) {
        return;
      }

      if (throttled(`h:${event}`)) {
        return;
      }

      triggerHaptic(event);
    },
    [throttled]
  );

  const fireSound = useCallback(
    (event: FeedbackEvent) => {
      if (!soundRef.current || hiddenRef.current || !audioReadyRef.current) {
        return;
      }

      if (throttled(`s:${event}`)) {
        return;
      }

      playSound(event);
    },
    [throttled]
  );

  const play = useCallback(
    (event: FeedbackEvent, opts?: PlayOptions) => {
      if (opts?.silent) {
        return;
      }

      fireHaptic(event);
      fireSound(event);
    },
    [fireHaptic, fireSound]
  );

  // Suppress sound when the tab is hidden (ref-based to avoid re-rendering the tree).
  useEventListener(
    "visibilitychange",
    () => {
      hiddenRef.current = document.visibilityState === "hidden";
    },
    { target: () => document }
  );

  // Document-level delegation + gesture-gated AudioContext bootstrap.
  useEffect(() => {
    const closestEvent = (target: EventTarget | null): FeedbackEvent | null => {
      if (!(target instanceof Element)) {
        return null;
      }

      const el = target.closest<HTMLElement>("[data-feedback]");
      const value = el?.dataset.feedback ?? null;

      return isDelegatedEvent(value) ? value : null;
    };

    const onPointerDown = (e: PointerEvent) => {
      // Lazily create + resume the AudioContext on the first user gesture.
      if (!audioReadyRef.current) {
        ensureReady()
          .then(() => {
            audioReadyRef.current = true;
          })
          .catch(() => {
            // No-op.
          });
      }

      const event = closestEvent(e.target);

      if (event) {
        fireHaptic(event);
      }
    };

    const onClick = (e: MouseEvent) => {
      const event = closestEvent(e.target);

      if (event) {
        fireSound(event);
      }
    };

    document.addEventListener("pointerdown", onPointerDown, { capture: true });
    document.addEventListener("click", onClick, { capture: true });

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, [fireHaptic, fireSound]);

  // Bridge: play feedback when a toast is added (success/error/warn/info).
  useEffect(() => {
    const subscribe = toastManager[
      " subscribe" as keyof typeof toastManager
    ] as
      | ((
          listener: (e: {
            action: string;
            options?: { type?: unknown };
          }) => void
        ) => () => void)
      | undefined;

    if (typeof subscribe !== "function") {
      return;
    }

    return subscribe((e) => {
      if (e.action !== "add") {
        return;
      }

      const event = toastTypeToEvent(e.options?.type);

      if (event) {
        play(event);
      }
    });
  }, [play]);

  const value = useMemo<FeedbackContextValue>(
    () => ({
      play,
      sound,
      haptic,
      setSound,
      setHaptic,
    }),
    [play, sound, haptic, setSound, setHaptic]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}
